<template>
  <div
    class="banner flex items-center h-12 gap-4 text-slate-25 dark:text-slate-25 text-xs py-3 px-4 justify-center"
    :class="bannerClasses"
  >
    <span class="banner-message">
      {{ bannerMessage }}
      <a
        v-if="hrefLink"
        :href="hrefLink"
        rel="noopener noreferrer nofollow"
        target="_blank"
      >
        {{ hrefLinkText }}
      </a>
    </span>
    <div class="actions">
      <woot-button
        v-if="hasActionButton"
        size="tiny"
        icon="arrow-right"
        :variant="actionButtonVariant"
        color-scheme="primary"
        class-names="banner-action__button"
        @click="onClick"
      >
        {{ actionButtonLabel }}
      </woot-button>
      <woot-button
        v-if="hasCloseButton"
        size="tiny"
        :color-scheme="colorScheme"
        icon="dismiss-circle"
        class-names="banner-action__button"
        @click="onClickClose"
      >
        {{ $t('GENERAL_SETTINGS.DISMISS') }}
      </woot-button>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    bannerMessage: {
      type: String,
      default: '',
    },
    hrefLink: {
      type: String,
      default: '',
    },
    hrefLinkText: {
      type: String,
      default: '',
    },
    hasActionButton: {
      type: Boolean,
      default: false,
    },
    actionButtonVariant: {
      type: String,
      default: '',
    },
    actionButtonLabel: {
      type: String,
      default: '',
    },
    colorScheme: {
      type: String,
      default: '',
    },
    hasCloseButton: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    bannerClasses() {
      const classList = [this.colorScheme];

      if (this.hasActionButton || this.hasCloseButton) {
        classList.push('has-button');
      }
      return classList;
    },
  },
  methods: {
    onClick(e) {
      this.$emit('click', e);
    },
    onClickClose(e) {
      this.$emit('close', e);
    },
  },
};
</script>

<style lang="scss" scoped>
.banner {
  // The `dark:` duplicates are intentional: the ramps flip in the variable layer,
  // so naming the same token twice keeps the fill correct in both modes.
  &.primary {
    @apply bg-woot-500 dark:bg-woot-500;
    .banner-action__button {
      @apply bg-woot-600 dark:bg-woot-600 border-none text-slate-25;

      &:hover {
        @apply bg-woot-700 dark:bg-woot-700;
      }
    }
  }

  // A quiet inline notice rather than an inverted bar: in dark mode this used to
  // stay a light grey slab, which left the accent action button on it unreadable.
  &.secondary {
    @apply bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-100;
    a {
      @apply text-slate-800 dark:text-slate-100;
    }
  }

  &.alert {
    // red-500 carried the near-white label at 3.75 / 3.09. Measured: red-700 is 5.04 in light,
    // and the dark label tier is dimmer so dark goes one step further to 5.53.
    @apply bg-red-700 dark:bg-red-800;
    .banner-action__button {
      @apply bg-red-800 dark:bg-red-900 border-none text-slate-25 dark:text-slate-25;

      &:hover {
        @apply bg-red-900 dark:bg-red-900;
      }
    }
  }

  // Yellow-on-yellow rendered the message invisible; the fill is bright enough to
  // need a near-black foreground, not its own colour.
  &.warning {
    @apply bg-yellow-500 dark:bg-yellow-500 text-yellow-900 dark:text-yellow-900;
    a {
      @apply text-yellow-900 dark:text-yellow-900;
    }
  }

  // No background of its own, so this sits on the PAGE — which is light in light mode. The base
  // `.banner` paints a near-white label for the inverted variants, and this never overrode it
  // for its action button: measured 1.00:1, an invisible control. Theme-aware now.
  &.gray {
    @apply text-slate-800 dark:text-slate-100;
    a,
    .banner-action__button {
      @apply text-slate-800 dark:text-slate-100;
    }
  }

  a {
    @apply ms-1 underline text-slate-25 dark:text-slate-25 text-xs rounded-sm;

    &:focus-visible {
      @apply outline-none ring-2 ring-accent-mint/70 ring-offset-2 ring-offset-white dark:ring-offset-ink;
    }

    &:focus:not(:focus-visible) {
      @apply outline-none;
    }
  }

  .banner-action__button {
    ::v-deep .button__content {
      @apply whitespace-nowrap;
    }
  }

  .banner-message {
    @apply flex items-center;
  }

  .actions {
    @apply flex gap-1;
  }
}
</style>
